import { AloftDate, getAbodeMonthFromDateString } from '@aloft/toolkit-shared';

import { CompSelectionProperty } from '../types.ts';
import { getStatusGroup } from './getStatusGroup.ts';
import { removeStreetSuffixes } from './removeStreetSuffixes.ts';

export const getCompSelectionListingKey = (comp: CompSelectionProperty): string =>
  (comp.listingID ?? comp.address?.AddressLineText?.trim().toLowerCase() ?? comp.id).toLowerCase();

const monthKeyFromDate = (date: AloftDate): string =>
  `${date.getYear()}-${String(date.getMonth()).padStart(2, '0')}`;

const getTransactionMonth = (comp: CompSelectionProperty): string | undefined => {
  if (comp.listingData?.CloseDate) return getAbodeMonthFromDateString(comp.listingData.CloseDate);
  if (comp.listingData?.ListingContractDate)
    return getAbodeMonthFromDateString(comp.listingData.ListingContractDate);
  if (comp.listingContractDate) return monthKeyFromDate(comp.listingContractDate);
  return undefined;
};

export const getCompSelectionMergeKey = (comp: CompSelectionProperty): string => {
  const address = comp.address?.AddressLineText?.trim();
  const statusGroup = getStatusGroup(comp.mlsStatus);
  if (address && statusGroup === 'active') {
    return `${removeStreetSuffixes(address).toLowerCase()}|active`;
  }
  const month = getTransactionMonth(comp);
  if (address && month) {
    return `${removeStreetSuffixes(address).toLowerCase()}|${month}|${statusGroup ?? ''}`;
  }
  return getCompSelectionListingKey(comp);
};

export const compMatchesRecommendation = (
  comp: CompSelectionProperty,
  rec: CompSelectionProperty,
): boolean => getCompSelectionMergeKey(comp) === getCompSelectionMergeKey(rec);

const getAddressKey = (comp: CompSelectionProperty): string | undefined =>
  comp.address?.AddressLineText
    ? removeStreetSuffixes(comp.address.AddressLineText).trim().toLowerCase()
    : undefined;

/**
 * Flags rows whose normalized address appears more than once in the list (e.g. a
 * property that was relisted/sold again and shows up as separate merge-key rows).
 * Runs after within-list dedup, so it only catches addresses that survive as distinct
 * rows rather than exact merge-key duplicates.
 */
export const flagDuplicateAddresses = (items: CompSelectionProperty[]): CompSelectionProperty[] => {
  const counts = new Map<string, number>();
  items.forEach((c) => {
    const key = getAddressKey(c);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  return items.map((c) => {
    const key = getAddressKey(c);
    return key && (counts.get(key) ?? 0) > 1 ? { ...c, hasMultipleListings: true } : c;
  });
};
